/**
 * Simple Games Counter Debugging Helper
 *
 * This is a simplified version that can be used immediately in the console
 * without waiting for store initialization.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- debug utility accessing Vue/Pinia internals
type VueInternal = any;

declare global {
  interface Window {
    gamesDebug: Record<string, unknown>;
    __VUE_DEVTOOLS_GLOBAL_HOOK__?: VueInternal;
  }
  interface HTMLElement {
    __vue_app__?: VueInternal;
  }
}

export function setupGamesDebug() {
  // Helper to safely unwrap refs or plain values
  const val = (v: unknown) =>
    v && typeof v === 'object' && 'value' in v
      ? (v as { value: unknown }).value
      : v;

  // Helper to locate a Pinia store instance by id without importing it
  function findStoreById(storeId: string): VueInternal | null {
    try {
      const appElement = document.querySelector('#app') as HTMLElement | null;
      const app = appElement?.__vue_app__;
      let pinia: VueInternal = null;

      if (app) {
        if (app._instance?.proxy?.$pinia) {
          pinia = app._instance.proxy.$pinia;
        } else if (app.config?.globalProperties?.$pinia) {
          pinia = app.config.globalProperties.$pinia;
        } else if (app._context?.provides?.pinia) {
          pinia = app._context.provides.pinia;
        }
      }

      if (!pinia && window.__VUE_DEVTOOLS_GLOBAL_HOOK__) {
        const hook = window.__VUE_DEVTOOLS_GLOBAL_HOOK__;
        const apps = hook.apps || [];
        for (const vueApp of apps) {
          if (vueApp._instance?.proxy?.$pinia) {
            pinia = vueApp._instance.proxy.$pinia;
            break;
          }
        }
      }

      if (pinia && pinia._s?.get) {
        return pinia._s.get(storeId) || null;
      }
    } catch (e) {
      // ignore
    }
    return null;
  }

  const debug = {
    // Check current state from localStorage
    checkLocalStorage: () => {
      const data = {
        gamesPlayedToday: localStorage.getItem('user.gamesPlayedToday'),
        lastPlayDate: localStorage.getItem('user.lastPlayDate'),
        user: localStorage.getItem('user.user'),
        completedPlays: localStorage.getItem('gameProgress.completed_plays'),
      };

      console.log('📦 LocalStorage State:');
      console.log('  Games Today:', data.gamesPlayedToday);
      console.log('  Last Play Date:', data.lastPlayDate);
      console.log('  GameProgress Completed:', data.completedPlays);

      // Check if user data is corrupted
      if (data.user === '[object Object]') {
        console.log(
          '  ❌ User data is corrupted (stored as "[object Object]")'
        );
        console.log('  This is preventing the counter from working!');
      } else {
        try {
          const user = data.user ? JSON.parse(data.user) : null;
          if (user) {
            console.log('  User completed_plays:', user.completed_plays);
          }
        } catch (e) {
          console.log('  ⚠️ Could not parse user data:', e.message);
          console.log('  Raw user data:', data.user?.substring(0, 100));
        }
      }

      return data;
    },

    // Monitor localStorage changes
    monitorChanges: () => {
      const originalSetItem = localStorage.setItem;
      localStorage.setItem = function (key, value) {
        // Check for corruption attempts
        if (key === 'user.user' && value === '[object Object]') {
          console.error('🚨 CORRUPTION ATTEMPT DETECTED!');
          console.error(
            '   Something is trying to store "[object Object]" in user.user'
          );
          console.error('   Stack trace:', new Error().stack);
          // Prevent the corruption
          console.log('   🚫 Blocking corrupt write');
          return;
        }

        if (
          key.includes('gamesPlayedToday') ||
          key.includes('completed_plays') ||
          key.includes('lastPlayDate') ||
          key === 'user.user'
        ) {
          console.log(`💾 LocalStorage Update: ${key}`);
          console.log(`   Old: ${localStorage.getItem(key)}`);
          console.log(
            `   New: ${value?.substring(0, 100)}${value?.length > 100 ? '...' : ''}`
          );
        }
        originalSetItem.call(localStorage, key, value);
      };
      console.log('🔍 Monitoring localStorage changes for games counter...');
      console.log('   Corruption protection enabled');
    },

    // Monitor API calls
    monitorAPI: () => {
      const originalFetch = window.fetch;
      window.fetch = async function (...args) {
        const url = args[0].toString();
        if (
          url.includes('process_trial') ||
          url.includes('set_game_level') ||
          url.includes('updateGamePlay')
        ) {
          console.log(`📤 API Call: ${url}`);

          const response = await originalFetch(...args);
          const clone = response.clone();

          try {
            const data = await clone.json();
            if ('completed_plays' in data) {
              console.log(
                `✅ API Response has completed_plays: ${data.completed_plays}`
              );
            } else {
              console.log(`⚠️ API Response missing completed_plays field`);
              console.log('   Response data:', data);
            }
          } catch (e) {
            console.log('   Could not parse response as JSON');
          }

          return response;
        }
        return originalFetch(...args);
      };
      console.log('🔍 Monitoring API calls for games counter...');
    },

    // Test localStorage update
    testLocalStorageUpdate: (count: number) => {
      console.log(`🧪 Testing localStorage update with count: ${count}`);
      localStorage.setItem('user.gamesPlayedToday', JSON.stringify(count));

      // Also update user object if it exists
      const userStr = localStorage.getItem('user.user');
      if (userStr === '[object Object]') {
        console.log('   ❌ User data is corrupted! Cannot update.');
        console.log('   Run gamesDebug.fixCorruptedUser() to fix this.');
      } else if (userStr) {
        try {
          const user = JSON.parse(userStr);
          user.completed_plays = count;
          localStorage.setItem('user.user', JSON.stringify(user));
          console.log(
            '   ✅ Updated both gamesPlayedToday and user.completed_plays'
          );

          // Now sync to Pinia stores if they exist
          debug.syncToStores();
        } catch (e) {
          console.log('   ⚠️ Could not update user object:', e.message);
        }
      } else {
        console.log('   ⚠️ No user data in localStorage');
      }
    },

    // Sync localStorage to Pinia stores
    syncToStores: () => {
      console.log('🔄 Syncing localStorage to Pinia stores...');

      try {
        // Use the same method as updateViaStore to find the store
        let userStore = null;
        const appElement = document.querySelector('#app');

        if (appElement && appElement?.__vue_app__) {
          const app = appElement?.__vue_app__;

          // Try different paths to find Pinia
          if (app._instance?.proxy?.$pinia) {
            userStore = app._instance.proxy.$pinia._s?.get?.('user');
          }

          if (!userStore && app.config?.globalProperties?.$pinia) {
            userStore = app.config.globalProperties.$pinia._s?.get?.('user');
          }

          if (!userStore && app._context?.provides?.pinia) {
            userStore = app._context.provides.pinia._s?.get?.('user');
          }
        }

        if (!userStore) {
          console.log('   ⚠️ UserStore not found');
          return;
        }

        // Read from localStorage
        const gamesPlayedToday = localStorage.getItem('user.gamesPlayedToday');
        const userStr = localStorage.getItem('user.user');

        if (gamesPlayedToday) {
          const count = JSON.parse(gamesPlayedToday);
          // Update the store's reactive values
          userStore.gamesPlayedToday = count;
          console.log(`   ✅ Updated store gamesPlayedToday to ${count}`);
        }

        if (userStr && userStr !== '[object Object]') {
          try {
            const userData = JSON.parse(userStr);
            // Update user object in store
            userStore.user = userData;
            console.log(
              `   ✅ Updated store user.completed_plays to ${userData.completed_plays}`
            );
          } catch (e) {
            console.log('   ⚠️ Could not parse user data');
          }
        }

        console.log('   ✅ Sync complete');
        console.log('   The HUD should now reflect the updated values');
      } catch (e) {
        console.log('   ❌ Error syncing to stores:', e.message);
        console.log('   You may need to use the store directly instead');
      }
    },

    // Clear games counter
    clearCounter: () => {
      console.log('🧹 Clearing games counter...');
      localStorage.setItem('user.gamesPlayedToday', '0');

      const userStr = localStorage.getItem('user.user');
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          user.completed_plays = 0;
          localStorage.setItem('user.user', JSON.stringify(user));
          console.log('   ✅ Reset counter to 0');
        } catch (e) {
          console.log('   ⚠️ Could not update user object');
        }
      }
    },

    // Force date reset
    forceDateReset: () => {
      console.log('📅 Forcing date reset...');
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];

      localStorage.setItem('user.lastPlayDate', JSON.stringify(yesterdayStr));
      console.log(`   Set last play date to: ${yesterdayStr}`);
      console.log('   Now reload the page or navigate to trigger reset check');
    },

    // Fix corrupted user data
    fixCorruptedUser: () => {
      console.log('🔧 Attempting to fix corrupted user data...');
      const userStr = localStorage.getItem('user.user');

      if (userStr === '[object Object]') {
        // Create a minimal valid user object
        const fixedUser = {
          id: '',
          first_name: 'User',
          last_name: '',
          name: 'User',
          email: '',
          gender: '',
          active: 1,
          tickets: 0,
          completed_plays: 0,
          loggedIn: true,
          features: {},
        };

        localStorage.setItem('user.user', JSON.stringify(fixedUser));
        console.log('   ✅ Created minimal valid user object');
        console.log(
          '   You may need to log out and log back in for full functionality'
        );
        return fixedUser;
      } else if (userStr) {
        try {
          const user = JSON.parse(userStr);
          console.log('   ℹ️ User data appears to be valid');
          return user;
        } catch (e) {
          console.log('   ❌ User data is corrupted but not "[object Object]"');
          console.log('   Consider clearing localStorage and logging in again');
        }
      } else {
        console.log('   ⚠️ No user data found');
      }
    },

    // Update counter using store methods
    updateViaStore: (count: number) => {
      console.log(`🎯 Updating counter via Pinia store to: ${count}`);

      try {
        // Multiple methods to try to access the store
        let userStore = null;
        let app = null;

        // Method 1: Try through __vue_app__
        const appElement = document.querySelector('#app');
        if (appElement && appElement?.__vue_app__) {
          app = appElement?.__vue_app__;
          console.log('   Found Vue app via __vue_app__');

          // Try different paths to Pinia
          if (app._instance?.proxy?.$pinia) {
            const pinia = app._instance.proxy.$pinia;
            userStore = pinia._s?.get?.('user');
            if (userStore) console.log('   Found store via $pinia');
          }

          if (!userStore && app.config?.globalProperties?.$pinia) {
            const pinia = app.config.globalProperties.$pinia;
            userStore = pinia._s?.get?.('user');
            if (userStore) console.log('   Found store via globalProperties');
          }

          if (!userStore && app._context?.provides?.pinia) {
            const pinia = app._context.provides.pinia;
            userStore = pinia._s?.get?.('user');
            if (userStore) console.log('   Found store via provides.pinia');
          }
        }

        // Method 2: Try through Vue DevTools hook
        if (!userStore && window.__VUE_DEVTOOLS_GLOBAL_HOOK__) {
          const hook = window.__VUE_DEVTOOLS_GLOBAL_HOOK__;
          const apps = hook.apps || [];
          for (const vueApp of apps) {
            if (vueApp._instance?.proxy?.$pinia) {
              const pinia = vueApp._instance.proxy.$pinia;
              userStore = pinia._s?.get?.('user');
              if (userStore) {
                console.log('   Found store via DevTools hook');
                break;
              }
            }
          }
        }

        if (!userStore) {
          console.log('   ⚠️ UserStore not found');
          console.log('   Debug info:');
          console.log('     App found:', !!app);
          if (app) {
            console.log('     Has _instance:', !!app._instance);
            console.log('     Has config:', !!app.config);
            console.log('     Has _context:', !!app._context);
          }
          return;
        }

        // Use the store's method
        if (typeof userStore.setGamesPlayedToday === 'function') {
          userStore.setGamesPlayedToday(count);
          console.log(
            '   ✅ Called userStore.setGamesPlayedToday(' + count + ')'
          );
        } else {
          // Fallback to direct property update
          userStore.gamesPlayedToday = count;
          if (userStore.user) {
            userStore.user.completed_plays = count;
          }
          console.log('   ✅ Updated store properties directly');
        }

        console.log('   The HUD should now show:', count, 'games');
      } catch (e) {
        console.error('   ❌ Error:', e.message);
        console.error('   Stack:', e.stack);
      }
    },

    // Start all monitoring
    startAll: () => {
      // First check for corrupted data
      const data = localStorage.getItem('user.user');
      if (data === '[object Object]') {
        console.warn('⚠️ Detected corrupted user data!');
        console.warn('   Run gamesDebug.fixCorruptedUser() to fix this');
      }

      debug.monitorChanges();
      debug.monitorAPI();
      console.log('✅ All monitoring started');
      console.log('📖 Available commands:');
      console.log('   gamesDebug.checkLocalStorage() - Check current state');
      console.log(
        '   gamesDebug.testLocalStorageUpdate(5) - Test localStorage update'
      );
      console.log('   gamesDebug.updateViaStore(5) - Update via Pinia store');
      console.log('   gamesDebug.syncToStores() - Sync localStorage to stores');
      console.log('   gamesDebug.clearCounter() - Reset counter to 0');
      console.log('   gamesDebug.forceDateReset() - Test daily reset');
      console.log('   gamesDebug.fixCorruptedUser() - Fix corrupted user data');
    },

    // Add cleanup function
    cleanupAndReload: () => {
      console.log('🧹 Cleaning up localStorage...');

      // Remove corrupted user data
      if (localStorage.getItem('user.user') === '[object Object]') {
        localStorage.removeItem('user.user');
        console.log('   Removed corrupted user.user');
      }

      // Clear games counter data for fresh start
      localStorage.removeItem('user.gamesPlayedToday');
      localStorage.removeItem('user.lastPlayDate');
      localStorage.removeItem('gameProgress.completed_plays');

      console.log('   ✅ Cleanup complete');
      console.log('   🔄 Reloading page...');

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    },

    // ===== Gameplay Round Controls =====
    // Get the gamePlay store instance if available
    getGamePlayStore: () => {
      const store = findStoreById('gamePlay');
      if (!store) {
        console.warn('⚠️ gamePlay store not found. Open a game screen first.');
      }
      return store;
    },

    // Inspect current gameplay round/trial info
    roundInfo: () => {
      const s = findStoreById('gamePlay');
      if (!s) return null;
      const info = {
        gameId: val(s.gameId),
        level: val(s.level),
        rounds: val(s.rounds),
        currentRound: val(s.currentRound),
        trials: val(s.trials),
        currentTrial: val(s.currentTrial),
        movedUpALevel: val(s.movedUpALevel),
      };
      console.log('🎯 Round Info:', info);
      return info;
    },

    // Set current round (resets trial and trial status safely)
    setRound: (roundNumber: number) => {
      const s = findStoreById('gamePlay');
      if (!s) {
        console.warn('⚠️ gamePlay store not found. Open a game screen first.');
        return;
      }
      const totalRounds = Math.max(1, Number(val(s.rounds) ?? 1));
      const newRound = Math.min(
        Math.max(1, Math.floor(Number(roundNumber) || 1)),
        totalRounds
      );

      // Apply state changes similar to a new round
      if (typeof s.setCurrentRound === 'function') s.setCurrentRound(newRound);
      if (typeof s.setCurrentTrial === 'function') s.setCurrentTrial(1);
      if (typeof s.setTrialsCorrect === 'function') s.setTrialsCorrect(0);
      if (typeof s.clearTrialStatus === 'function') s.clearTrialStatus();
      if (typeof s.setCorrectThisRound === 'function') s.setCorrectThisRound(0);
      if (typeof s.setMovedUpALevel === 'function') s.setMovedUpALevel(false);
      if (typeof s.setChangedRound === 'function') s.setChangedRound(true);

      const info = {
        currentRound: val(s.currentRound),
        currentTrial: val(s.currentTrial),
        rounds: val(s.rounds),
        trials: val(s.trials),
      };
      console.log('✅ Set round:', info);
      console.log(
        'ℹ️ Note: Per-game components may refresh settings on round change.'
      );
      return info;
    },

    // Convenience helpers
    nextRound: () => {
      const s = findStoreById('gamePlay');
      if (!s) return;
      const current = Number(val(s.currentRound) ?? 1);
      const total = Number(val(s.rounds) ?? 1);
      if (current >= total) {
        console.warn('⚠️ Already at last round');
        return;
      }
      return window.gamesDebug.setRound(current + 1);
    },

    prevRound: () => {
      const s = findStoreById('gamePlay');
      if (!s) return;
      const current = Number(val(s.currentRound) ?? 1);
      if (current <= 1) {
        console.warn('⚠️ Already at first round');
        return;
      }
      return window.gamesDebug.setRound(current - 1);
    },

    // Optionally set the current trial within the round
    setTrial: (trialNumber: number) => {
      const s = findStoreById('gamePlay');
      if (!s) return;
      const totalTrials = Math.max(1, Number(val(s.trials) ?? 1));
      const newTrial = Math.min(
        Math.max(1, Math.floor(Number(trialNumber) || 1)),
        totalTrials
      );
      if (typeof s.setCurrentTrial === 'function') s.setCurrentTrial(newTrial);
      if (newTrial === 1 && typeof s.clearTrialStatus === 'function')
        s.clearTrialStatus();
      const info = {
        currentRound: val(s.currentRound),
        currentTrial: val(s.currentTrial),
        trials: val(s.trials),
      };
      console.log('✅ Set trial:', info);
      return info;
    },
  };

  // Expose to window
  window.gamesDebug = debug;

  console.log('🎮 Games Debug Helper Ready!');
  console.log('📖 Type: gamesDebug.startAll() to begin monitoring');
  console.log('📖 Type: gamesDebug.checkLocalStorage() to check current state');
  console.log('🎯 Gameplay round controls:');
  console.log('   gamesDebug.roundInfo()');
  console.log('   gamesDebug.setRound(3) // also resets trial');
  console.log('   gamesDebug.nextRound() / gamesDebug.prevRound()');
  console.log('   gamesDebug.setTrial(1)');
  console.log(
    '🧩 If user data is corrupted, type: gamesDebug.cleanupAndReload()'
  );

  return debug;
}

// Auto-setup when imported
if (typeof window !== 'undefined') {
  setupGamesDebug();
}
